<template>
    <div class="sorting-list">
        <div class="flex justify-center items-center">
            <XCheckbox
                @input="toggleSortingMatchOption('complete')"
                :checked="answers[0] && answers[0].data.completely_correct"
                :disabled="!canEditQuiz"
                type="radio"
                size="md"
                theme="dark"
            >
                <span class="font-size-14 no-underline flex items-center mr-2 shadow-text-none">
                    {{ $t('question_completely_correct') }}
                </span>
            </XCheckbox>
            <XCheckbox
                @input="toggleSortingMatchOption('percentage')"
                :checked="answers[0] && !answers[0].data.completely_correct"
                :disabled="!canEditQuiz"
                type="radio"
                size="md"
                theme="dark"
            >
                <span class="font-size-14 no-underline flex items-center mr-2 shadow-text-none">
                    {{ $t('question_percentage') }}
                </span>
            </XCheckbox>
        </div>

        <div
            v-if="answers[0] && !answers[0].data.completely_correct"
            class="percent-match-box flex justify-center items-center mt-4"
        >
            <label :for="`sorting_accept_percent-${questionIndex}`" class="font-size-14 w-auto">
                {{ $t('question_percentage_match') }}
            </label>
            <XInput
                v-validate="'required|min:0|max:100'"
                @blur="onChangeMatchPercent($event)"
                :id="`sorting_accept_percent-${questionIndex}`"
                :name="`sorting_accept_percent-${questionIndex}`"
                :value="answers[0].data.accepted_percentage"
                :disabled="!canEditQuiz"
                data-vv-as="accepted percentage"
                class="input percentage-input"
                type="number"
                min="0"
                max="100"
            />
            <span class="shadow-text-none">%</span>
        </div>
        <span v-if="errors.has('sorting_accept_percent-' + questionIndex + '-0')" class="text-center has-error mt-1">
            {{ errors.first('sorting_accept_percent-' + questionIndex + '-0') }}
        </span>

        <p class="text-center mt-4 font-size-16 shadow-text-none">
            <i class="fas fa-arrows-alt"></i> {{ $t('sorting_dragdrop_order_answer') }}
        </p>
        <div class="answers-section">
            <draggable @end="endSorting()" v-model="cacheAnswers" handle=".move-handle" ghostClass="sortable-ghost">
                <div v-for="(answer, index) in cacheAnswers" :key="answer.id">
                    <QuizCreatorImage
                        @remove="removeAnswerImage(index)"
                        :ref="`answer-image-${index}`"
                        :id="`answer-image-${questionIndex}-${index}`"
                        :class="{ 'pointer-events-none': !canEditQuiz }"
                        :image="answer.image"
                        :image-original="answer.image_original"
                        :image-source="answer.image_source"
                        :image-coordinates="answer.image_coordinates"
                        location="answer"
                        color="#ccc"
                        border-color="#ccc"
                        border-style="dashed"
                    />

                    <div class="relative mt-4">
                        <XInput
                            v-validate="{ required: !answer.image }"
                            @input="inputAnswer($event, index)"
                            :value="answer.title"
                            :name="`answer-${questionIndex}-${index}`"
                            :placeholder="$t('question_placeholder_choice')"
                            :disabled="!canEditQuiz"
                            data-vv-as="choice"
                            autocomplete="off"
                            maxlength="50"
                            class="input-text-alignment"
                        />
                        <span class="move-handle cursor-move flex justify-center items-center text-white">
                            <i class="fas fa-arrows-alt-v"></i>
                        </span>
                        <div class="input-option-btn flex absolute">
                            <span @click="openUploadMedia(questionIndex, index)">
                                <i class="far fa-image image-icon shadow-text-none cursor-pointer"></i>
                            </span>
                            <span v-if="answers.length > 1" @click="removeAnswer(index)">
                                <i class="far fa-trash-alt image-icon shadow-text-none cursor-pointer"></i>
                            </span>
                        </div>
                    </div>

                    <span
                        v-if="errors.has(`answer-${questionIndex}-${index}`) && !answer.title"
                        class="has-error text-red text-center mt-2 font-size-14 shadow-text-none"
                    >
                        * {{ errors.first(`answer-${questionIndex}-${index}`) }}
                    </span>
                    <label class="text-white hidden"> <input type="checkbox" />Make this correct answer </label>
                </div>
            </draggable>
            <div class="mt-4">
                <XButton
                    @click="addAnswer()"
                    :disabled="!canEditQuiz"
                    class="w-full cursor-pointer shadow-text-none"
                    bg-color="#f7f8fa"
                    color="#000"
                    font-weight="400"
                >
                    <span>{{ $t('question_modal_button_add_choice') }}</span>
                </XButton>
            </div>
        </div>
    </div>
</template>

<script>
import quizCreator from '@/mixins/quiz-creator.js'
import draggable from 'vuedraggable'
import XCheckbox from '@/components/v2/XCheckbox'
import XInput from '@/components/v2/XInput'
import XButton from '@/components/v2/XButton'
import QuizCreatorImage from '@/components/quiz-creator/QuizCreatorImage'

export default {
    components: {
        draggable,
        XCheckbox,
        XInput,
        XButton,
        QuizCreatorImage
    },

    mixins: [quizCreator],

    props: {
        questionIndex: {
            type: Number,
            required: true
        },

        answers: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            cacheAnswers: null
        }
    },

    created() {
        this.cacheAnswers = this.answers
    },

    watch: {
        answers: {
            handler(data) {
                this.cacheAnswers = data
            },

            deep: true
        }
    },

    methods: {
        toggleSortingMatchOption(value) {
            if (!this.canEditQuiz) return

            this.$store.commit('toggleSortingMatchOption', { questionIndex: this.questionIndex, value })
        },

        onChangeMatchPercent(event) {
            if (!this.canEditQuiz) return

            let value = event.target.value
            const min = 0
            const max = 100

            if (value < min) {
                value = min
            }

            if (value > max) {
                value = max
            }

            this.$store.commit('updateSortingMatchPercent', { questionIndex: this.questionIndex, value })
        },

        endSorting() {
            if (!this.canEditQuiz) return

            this.$store.commit('updateSortingList', { questionIndex: this.questionIndex, answers: this.cacheAnswers })
            this.$store.commit('afterDropSortingList', { questionIndex: this.questionIndex })
        }
    }
}
</script>

<style lang="scss">
.sorting-list {
    .move-handle {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        background-color: #49403e;

        i {
            width: auto;
            height: auto;
            padding: 0;
            background-color: unset;
        }
    }
}
</style>
